import { InvoiceRunStatus, InvoiceRunType } from '@/graphql/generatedTypes';

/**
 * Presentation helpers for the Monthly invoicing screens.
 *
 * The backend exposes four statuses (PENDING / RUNNING / DONE / FAILED); the
 * UI presents six states — see the design handoff §6:
 *   Scheduled = PENDING && due_at in the future (calm, "Upcoming" only)
 *   Starting  = PENDING && due_at passed (transient, amber)
 *   Running   = RUNNING with a heartbeat within the last 2 h
 *   Stalled   = RUNNING without a heartbeat for more than 2 h
 *   Done / Failed = as-is
 * "Charged" is a badge on generation runs whose charge run is DONE.
 *
 * All timestamps are ISO strings and are always displayed Europe/Berlin.
 */

export const BERLIN_TZ = 'Europe/Berlin';

export const STALL_THRESHOLD_MS = 2 * 60 * 60 * 1000;

export type RunPresentationState =
  | 'scheduled'
  | 'starting'
  | 'running'
  | 'stalled'
  | 'done'
  | 'failed';

type RunStateFields = {
  status: InvoiceRunStatus;
  due_at?: string | null;
  last_activity_at?: string | null;
  started_at?: string | null;
};

export function runPresentationState(
  run: RunStateFields,
  now: number = Date.now(),
): RunPresentationState {
  switch (run.status) {
    case InvoiceRunStatus.Pending:
      // A pending charge run has no due_at — it is due as soon as its parent
      // is done, so it presents as Starting, not Scheduled.
      return run.due_at && Date.parse(run.due_at) > now
        ? 'scheduled'
        : 'starting';
    case InvoiceRunStatus.Running: {
      const heartbeat = run.last_activity_at ?? run.started_at;
      return heartbeat && now - Date.parse(heartbeat) > STALL_THRESHOLD_MS
        ? 'stalled'
        : 'running';
    }
    case InvoiceRunStatus.Done:
      return 'done';
    case InvoiceRunStatus.Failed:
      return 'failed';
  }
}

type ChargeRunLink = { status: InvoiceRunStatus } | null | undefined;

export function isGenerationRun(run: { type: InvoiceRunType }) {
  return run.type !== InvoiceRunType.CompleteAndCharge;
}

export function isCharged(run: {
  type: InvoiceRunType;
  charge_run?: ChargeRunLink;
}) {
  return (
    isGenerationRun(run) && run.charge_run?.status === InvoiceRunStatus.Done
  );
}

/** Done, live (not dry), generation run without a successful/ongoing charge. */
export function isReadyToCharge(run: {
  type: InvoiceRunType;
  status: InvoiceRunStatus;
  dry_run: boolean;
  charge_run?: ChargeRunLink;
}) {
  if (!isGenerationRun(run)) return false;
  if (run.status !== InvoiceRunStatus.Done || run.dry_run) return false;
  const charge = run.charge_run;
  return !charge || charge.status === InvoiceRunStatus.Failed;
}

/** A collection draft regeneration the worker has not finished yet. */
export const isRegenerationOpen = (regeneration: {
  status: InvoiceRunStatus;
}) =>
  regeneration.status === InvoiceRunStatus.Pending ||
  regeneration.status === InvoiceRunStatus.Running;

/** Should the runs query poll / the live chip show? */
export function isRunActive(
  run: RunStateFields,
  now: number = Date.now(),
): boolean {
  const state = runPresentationState(run, now);
  return state === 'starting' || state === 'running' || state === 'stalled';
}

// ---------------------------------------------------------------------------
// Billing periods
// ---------------------------------------------------------------------------

/**
 * ES billing periods run from the 25th of the previous month to the 24th of
 * the month named by (year, iso_month); every other country bills the plain
 * calendar month. iso_month is 0-based (JS Date#getMonth convention).
 */
export function isEsStylePeriod(run: {
  type: InvoiceRunType;
  parent_run?: { type: InvoiceRunType } | null;
}): boolean {
  return (
    run.type === InvoiceRunType.Es || run.parent_run?.type === InvoiceRunType.Es
  );
}

const monthShort = (year: number, isoMonth: number) =>
  new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, isoMonth, 15)));

const monthLong = (year: number, isoMonth: number) =>
  new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, isoMonth, 15)));

/** "August 2026" (default) / "25 Jul - 24 Aug 2026" (ES). */
export function periodLabel(
  esStyle: boolean,
  year: number,
  isoMonth: number,
): string {
  if (!esStyle) {
    return `${monthLong(year, isoMonth)} ${year}`;
  }
  const end = new Date(Date.UTC(year, isoMonth, 24));
  const start = new Date(Date.UTC(year, isoMonth - 1, 25));
  const startYear = start.getUTCFullYear();
  const endYear = end.getUTCFullYear();
  const startPart = `25 ${monthShort(startYear, start.getUTCMonth())}${
    startYear !== endYear ? ` ${startYear}` : ''
  }`;
  return `${startPart} - 24 ${monthShort(endYear, end.getUTCMonth())} ${endYear}`;
}

/** Detail subline: "1 - 31 August 2026" (default) / same as periodLabel (ES). */
export function periodRangeLabel(
  esStyle: boolean,
  year: number,
  isoMonth: number,
): string {
  if (esStyle) return periodLabel(true, year, isoMonth);
  const lastDay = new Date(Date.UTC(year, isoMonth + 1, 0)).getUTCDate();
  return `1 - ${lastDay} ${monthLong(year, isoMonth)} ${year}`;
}

export type PeriodOption = {
  year: number;
  isoMonth: number;
  value: string;
  label: string;
  current: boolean;
};

function berlinToday(): { year: number; month: number; day: number } {
  // en-CA yields YYYY-MM-DD.
  const [y, m, d] = new Intl.DateTimeFormat('en-CA', {
    timeZone: BERLIN_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(new Date())
    .split('-')
    .map(Number);
  return { year: y, month: m - 1, day: d };
}

export const periodValue = (year: number, isoMonth: number) =>
  `${year}-${isoMonth}`;

export function parsePeriodValue(value: string): {
  year: number;
  isoMonth: number;
} {
  const [year, isoMonth] = value.split('-').map(Number);
  return { year, isoMonth };
}

/**
 * Recent billing periods for the start-run form, newest first. The first
 * entry is the period that just ended ("current" — the one a catch-up run
 * would normally target); older entries exist to catch up on missed periods.
 */
export function recentPeriodOptions(
  type: InvoiceRunType,
  count = 6,
): PeriodOption[] {
  const today = berlinToday();
  const esStyle = type === InvoiceRunType.Es;

  // Anchor = the (year, iso_month) of the most recently *ended* period.
  // ES period (y, m) ends on 24 m; the default period (y, m) ends with m.
  let anchor: Date;
  if (esStyle) {
    anchor =
      today.day > 24
        ? new Date(Date.UTC(today.year, today.month, 1))
        : new Date(Date.UTC(today.year, today.month - 1, 1));
  } else {
    anchor = new Date(Date.UTC(today.year, today.month - 1, 1));
  }

  return Array.from({ length: count }, (_, i) => {
    const d = new Date(
      Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() - i, 1),
    );
    const year = d.getUTCFullYear();
    const isoMonth = d.getUTCMonth();
    return {
      year,
      isoMonth,
      value: periodValue(year, isoMonth),
      label: periodLabel(esStyle, year, isoMonth),
      current: i === 0,
    };
  });
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** First 8 chars of the run UUID — the short mono id shown everywhere. */
export const shortRunId = (id: string) => id.slice(0, 8);

/** 447 → "ES-0447" (display only — the API takes the plain integer). */
export const formatEsNumber = (n: number) => `ES-${String(n).padStart(4, '0')}`;

const berlinFormat = (iso: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: BERLIN_TZ, ...options }).format(
    new Date(iso),
  );

/** "24 Aug, 21:11" */
export const formatDateTimeShort = (iso: string) =>
  `${berlinFormat(iso, { day: 'numeric', month: 'short' })}, ${berlinFormat(
    iso,
    { hour: '2-digit', minute: '2-digit', hour12: false },
  )}`;

/** "24 Aug" */
export const formatDayMonth = (iso: string) =>
  berlinFormat(iso, { day: 'numeric', month: 'short' });

/** "24 Sep 2026" */
export const formatDateLong = (iso: string) =>
  berlinFormat(iso, { day: 'numeric', month: 'short', year: 'numeric' });

/** "24 Aug 2026, 21:11" — right-rail timeline rows. */
export const formatTimestamp = (iso: string) =>
  `${formatDateLong(iso)}, ${berlinFormat(iso, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })}`;

/** "21:04" */
export const formatTimeOnly = (iso: string) =>
  berlinFormat(iso, { hour: '2-digit', minute: '2-digit', hour12: false });

/** "2 h 14 min" / "42 min" / "less than a minute" */
export function formatDurationMs(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'less than a minute';
  const hours = Math.floor(minutes / 60);
  if (hours < 1) return `${minutes} min`;
  return `${hours} h ${minutes % 60} min`;
}

/** "12 s ago" / "3 min ago" — the live chip. */
export function formatAgo(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds} s ago`;
  return `${Math.floor(seconds / 60)} min ago`;
}

/** "12 s ago" for a past epoch timestamp, measured against now. */
export const formatAgoSince = (epochMs: number) =>
  formatAgo(Date.now() - epochMs);

/** How long a running run has gone without a heartbeat, in ms. */
export function heartbeatSilenceMs(run: {
  last_activity_at?: string | null;
  started_at?: string | null;
}): number | null {
  const heartbeat = run.last_activity_at ?? run.started_at;
  return heartbeat ? Date.now() - Date.parse(heartbeat) : null;
}

/** "today" / "yesterday" / "24 Aug 2026" — resolved-by lines. */
export function formatRelativeDay(iso: string): string {
  const dayOf = (d: Date) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: BERLIN_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  const day = dayOf(new Date(iso));
  const now = new Date();
  if (day === dayOf(now)) return 'today';
  if (day === dayOf(new Date(now.getTime() - 24 * 60 * 60 * 1000)))
    return 'yesterday';
  return formatDateLong(iso);
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export const formatCount = (n: number) => n.toLocaleString('en-GB');

export function runTypeLabel(type: InvoiceRunType): string {
  switch (type) {
    case InvoiceRunType.Es:
      return 'Spanish';
    case InvoiceRunType.Default:
      return 'Default';
    case InvoiceRunType.CompleteAndCharge:
      return 'Complete & charge';
  }
}

export function runTypeLongLabel(type: InvoiceRunType): string {
  switch (type) {
    case InvoiceRunType.Es:
      return 'Spanish run';
    case InvoiceRunType.Default:
      return 'Default run';
    case InvoiceRunType.CompleteAndCharge:
      return 'Complete & charge';
  }
}

/** "schedule" from the backend means the cron started it, not a person. */
export const triggeredByLabel = (requestedBy: string | null | undefined) =>
  !requestedBy || requestedBy === 'schedule'
    ? 'Schedule (automatic)'
    : requestedBy;

/** Normalize for the type-the-period charge friction: trim + lowercase. */
export const normalizePeriodInput = (value: string) =>
  value.trim().toLowerCase();
